/**
 * Create / edit appointment form (walk-in + reschedule).
 * Compact: preview + cliente picker + free-text name/phone + turno pickers.
 * Mobile sheet + desktop dialog; pickers via MasterFilterSheetModal (ticket).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { formatArPhoneDisplay, nationalPartFromStored } from '@shared/phone';
import { createTodayDate, formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import { fetchCustomerAccounts } from '../../customers/public-api'
import { openCustomerNewCustomer } from '../../customers/public-api'
import type { CustomerAccountRow } from '../../customers/public-api';
import { openCatalogNewService } from '../../catalog/app-api';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { CONTENT_MAX_WIDTH_FORM } from '../../shared/layout/breakpoints';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { appToastConfig, showError, showSuccessAfterModalClose, toastTopOffset } from '../../shared/ui/appToast';
import { VueltiFeliz } from '../../shared/ui/VueltiFeliz';
import { hapticSuccess } from '../../shared/ui/haptics';
import { trackOnboardingFunnel } from '../../onboarding/application/onboardingFunnel';
import { invalidateDay1Evidence } from '../../onboarding/application/day1PathEvidence';
import { useDay1PathNavStore } from '../../onboarding/store/useDay1PathNavStore';
import { openAppointmentResources } from '../application/appointmentNavigation';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { agenda, agendaFonts, agendaPaperSheet } from '../domain/agendaTheme';
import {
  openSlotOptionsForResource,
  filterSlotsByAppointments,
  findNextOpenSlot,
  snapToOpenSlot,
} from '../domain/slotOptions';
import type {
  Appointment,
  AppointmentResource,
  BookingSettings,
  ResourceTimeOff,
  ServiceProductOption,
} from '../domain/types';
import {
  entitlementCoversProducts,
  type CustomerPackageEntitlement,
} from '../domain/servicePackageTypes';
import {
  sumAppointmentItemDurations,
  sumAppointmentItemPrices,
} from '../domain/appointmentItems';
import {
  createWalkInAppointment,
  listServiceProducts,
  listResourceServiceMap,
  listBranchAppointments,
  listBranchResourceTimeOff,
  getBookingSettings,
  getCustomerAppointmentStats,
  updateAppointment,
} from '../infra/appointmentsRepo';
import { listCustomerPackageEntitlements } from '../infra/servicePackagesRepo';
import { formatMoney } from '../../shared/format/public-api';

function formatDayLong(d: Date): string {
  const s = d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function toHm(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseHm(value: string): { h: number; m: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h, m: min };
}


function addMinutesHm(hm: string, minutes: number): string | null {
  const parsed = parseHm(hm);
  if (!parsed) return null;
  const total = parsed.h * 60 + parsed.m + minutes;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDayShort(d: Date): string {
  return d.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
}

function dayOptionLabel(d: Date, today: Date): string {
  const diff = Math.round((startOfLocalDay(d).getTime() - today.getTime()) / 86_400_000);
  const short = formatDayShort(d);
  if (diff === 0) return `Hoy · ${short}`;
  if (diff === 1) return `Mañana · ${short}`;
  return short;
}

/** Días que escanea el hint "Próximo disponible" cuando el día elegido está lleno. */
const NEXT_SLOT_SCAN_DAYS = 14;
/** Días hacia adelante que ofrece el picker de fecha. */
const DAY_OPTIONS_AHEAD = 60;

export type AppointmentFormPrefill = { resourceId?: string; startsAt?: Date; day?: Date };

type Props = {
  visible: boolean;
  mode: 'create' | 'edit';
  editing?: Appointment | null;
  orgId?: string;
  branchId?: string;
  resources: AppointmentResource[];
  /** Time-off overlapping the form day (filtered by selected resource). */
  timeOffs?: ResourceTimeOff[];
  /** Existing appointments for the day (used to filter occupied slots). */
  appointments?: Appointment[];
  day: Date;
  prefill: AppointmentFormPrefill | null;
  isWide: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type PickerKind = 'client' | 'service' | 'staff' | 'time' | 'package' | 'date' | null;

export function AppointmentFormModal(props: Props) {
  const navigation = useNavigation();
  const { canManageAgenda } = useBranchPermission('Agenda');
  const hasServicePackages = useHasBusinessCapability('service_packages');
  const [services, setServices] = useState<ServiceProductOption[]>([]);
  const [clients, setClients] = useState<CustomerAccountRow[]>([]);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [timeHm, setTimeHm] = useState('10:00');
  const [saving, setSaving] = useState(false);
  const [resourceServiceMap, setResourceServiceMap] = useState<Map<string, string[]>>(new Map());
  const [showMoreCustomer, setShowMoreCustomer] = useState(false);
  const [openPicker, setOpenPicker] = useState<PickerKind>(null);
  const [entitlements, setEntitlements] = useState<CustomerPackageEntitlement[]>([]);
  const [packageEntitlementId, setPackageEntitlementId] = useState<string | null>(null);
  // Fecha del turno (proto v-form): create default = día del board, edit = fecha del turno.
  const [formDay, setFormDay] = useState<Date>(() => props.day);
  const [notes, setNotes] = useState('');
  const [customerStats, setCustomerStats] = useState<{ visits: number; noShows: number } | null>(
    null,
  );
  const [bookingSettings, setBookingSettings] = useState<BookingSettings | null>(null);
  // Turnos/ausencias del día elegido cuando no es el día del board (props solo cubren ése).
  const [remoteDay, setRemoteDay] = useState<{
    key: string;
    appointments: Appointment[];
    timeOffs: ResourceTimeOff[];
  } | null>(null);
  const [nextSlot, setNextSlot] = useState<{ day: Date; hm: string } | null>(null);
  // Cerrar con datos escritos pide confirmar — un tap erróneo no borra el draft.
  const [discardVisible, setDiscardVisible] = useState(false);
  // Primer hito día 1 (proto opción B): éxito in-modal en vez del toast.
  const [milestoneDone, setMilestoneDone] = useState<{
    customerName: string;
    servicesLabel: string;
    timeHm: string;
    endsHm: string | null;
    staffName: string;
    priceLabel: string | null;
  } | null>(null);

  const dayOfWeek = formDay.getDay();
  const formDayIso = formatIsoDate(formDay);
  const isBoardDay = sameLocalDay(formDay, props.day);
  const remoteDayData = remoteDay && remoteDay.key === formDayIso ? remoteDay : null;
  const eligibleResources = useMemo(() => {
    if (productIds.length === 0) return props.resources;
    return props.resources.filter((r) => {
      const assigned = resourceServiceMap.get(r.id);
      if (!assigned) return true; // no map rows → offers all
      return productIds.every((pid) => assigned.includes(pid));
    });
  }, [props.resources, productIds, resourceServiceMap]);

  const selectedResource = eligibleResources.find((r) => r.id === resourceId) ?? null;
  const resourceTimeOffs = useMemo(() => {
    const src = isBoardDay ? (props.timeOffs ?? []) : (remoteDayData?.timeOffs ?? []);
    return src.filter((t) => t.resourceId === selectedResource?.id);
  }, [isBoardDay, props.timeOffs, remoteDayData, selectedResource?.id]);
  const selectedServices = useMemo(
    () => productIds.map((id) => services.find((s) => s.id === id)).filter(Boolean) as ServiceProductOption[],
    [productIds, services],
  );
  const totalDuration = useMemo(
    () => sumAppointmentItemDurations(selectedServices.map((s) => ({ durationMinutes: s.durationMinutes }))),
    [selectedServices],
  );
  const totalPrice = useMemo(
    () => sumAppointmentItemPrices(selectedServices.map((s) => ({ price: s.price }))),
    [selectedServices],
  );
  const primaryProductId = productIds[0] ?? null;
  const selectedService = selectedServices[0] ?? null;
  const rawSlotOptions = useMemo(
    () => openSlotOptionsForResource(dayOfWeek, selectedResource, formDay, resourceTimeOffs),
    [dayOfWeek, selectedResource, formDay, resourceTimeOffs],
  );
  const formDayAppointments = useMemo(() => {
    const src = isBoardDay ? (props.appointments ?? []) : (remoteDayData?.appointments ?? []);
    return src.filter((a) => sameLocalDay(new Date(a.startsAt), formDay));
  }, [isBoardDay, props.appointments, remoteDayData, formDay]);
  const slotOptions = useMemo(
    () =>
      filterSlotsByAppointments(
        rawSlotOptions,
        formDayAppointments,
        resourceId,
        totalDuration,
        props.mode === 'edit' ? props.editing?.id : undefined,
      ),
    [rawSlotOptions, formDayAppointments, resourceId, totalDuration, props.mode, props.editing?.id],
  );

  const singleAgenda = eligibleResources.length === 1;

  useEffect(() => {
    if (!props.visible || !props.orgId) return;
    void listServiceProducts(props.orgId)
      .then((list) => {
        setServices(list);
        if (props.mode === 'edit' && props.editing) {
          const fromItems = props.editing.items?.map((i) => i.productId).filter(Boolean);
          setProductIds(
            fromItems && fromItems.length > 0 ? fromItems : [props.editing.productId].filter(Boolean),
          );
        } else {
          setProductIds([]);
        }
      })
      .catch(() => setServices([]));

    void listResourceServiceMap(props.orgId)
      .then(setResourceServiceMap)
      .catch(() => setResourceServiceMap(new Map()));

    // ponytail: load once per open; search is in-sheet via MasterFilterSheetModal
    void fetchCustomerAccounts({ limit: 300, query: {} })
      .then(setClients)
      .catch(() => setClients([]));

    if (props.mode === 'edit' && props.editing) {
      const ed = props.editing;
      setResourceId(ed.resourceId);
      setCustomerId(ed.customerId);
      setCustomerName(ed.customerName);
      setCustomerPhone(nationalPartFromStored(ed.customerPhone ?? ''));
      setCustomerEmail(ed.customerEmail ?? '');
      setTimeHm(toHm(new Date(ed.startsAt)));
      setFormDay(new Date(ed.startsAt));
      setNotes(ed.notes ?? '');
    } else {
      const prefResource =
        (props.prefill?.resourceId &&
          props.resources.find((r) => r.id === props.prefill?.resourceId)?.id) ||
        props.resources[0]?.id ||
        null;
      setResourceId(prefResource);
      if (props.prefill?.startsAt) {
        setTimeHm(toHm(props.prefill.startsAt));
        setFormDay(props.prefill.startsAt);
      } else {
        setTimeHm('10:00');
        setFormDay(props.prefill?.day ?? props.day);
      }
      setCustomerId(null);
      setCustomerName('');
      setCustomerPhone('');
      setCustomerEmail('');
      setNotes('');
    }
    setShowMoreCustomer(false);
    setOpenPicker(null);
    setDiscardVisible(false);
    setPackageEntitlementId(null);
    setEntitlements([]);
    setMilestoneDone(null);
    setRemoteDay(null);
    setNextSlot(null);
    setCustomerStats(null);
    setBookingSettings(null);
  }, [props.visible, props.orgId, props.resources, props.day, props.prefill, props.mode, props.editing]);

  useEffect(() => {
    if (!props.visible || !hasServicePackages || !customerId) {
      setEntitlements([]);
      setPackageEntitlementId(null);
      return;
    }
    let cancelled = false;
    void listCustomerPackageEntitlements({ customerId, includeInactive: false })
      .then((rows) => {
        if (cancelled) return;
        setEntitlements(rows);
      })
      .catch(() => {
        if (!cancelled) setEntitlements([]);
      });
    return () => {
      cancelled = true;
    };
  }, [props.visible, hasServicePackages, customerId]);

  useEffect(() => {
    if (!packageEntitlementId) return;
    const stillValid = entitlements.some(
      (e) => e.id === packageEntitlementId && entitlementCoversProducts(e, productIds),
    );
    if (!stillValid) setPackageEntitlementId(null);
  }, [entitlements, packageEntitlementId, productIds]);

  // Fecha fuera del día del board: turnos + ausencias del día elegido.
  useEffect(() => {
    if (!props.visible || !props.branchId || isBoardDay) {
      setRemoteDay(null);
      return;
    }
    let cancelled = false;
    const branchId = props.branchId;
    const from = startOfLocalDay(formDay);
    const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
    void Promise.all([
      listBranchAppointments(branchId, from.toISOString(), to.toISOString(), null),
      listBranchResourceTimeOff(branchId, from.toISOString(), to.toISOString()),
    ])
      .then(([appointments, timeOffs]) => {
        if (!cancelled) setRemoteDay({ key: formDayIso, appointments, timeOffs });
      })
      .catch(() => {
        if (!cancelled) setRemoteDay({ key: formDayIso, appointments: [], timeOffs: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [props.visible, props.branchId, formDayIso, isBoardDay, formDay]);

  // "Próximo disponible": solo cuando el día elegido no tiene slots libres.
  useEffect(() => {
    if (!props.visible || !props.branchId || !selectedResource || slotOptions.length > 0) {
      setNextSlot(null);
      return;
    }
    let cancelled = false;
    const branchId = props.branchId;
    const resource = selectedResource;
    const from = new Date(formDay.getFullYear(), formDay.getMonth(), formDay.getDate() + 1);
    const to = new Date(
      formDay.getFullYear(),
      formDay.getMonth(),
      formDay.getDate() + NEXT_SLOT_SCAN_DAYS + 1,
    );
    void Promise.all([
      listBranchAppointments(branchId, from.toISOString(), to.toISOString(), resource.id),
      listBranchResourceTimeOff(branchId, from.toISOString(), to.toISOString()),
    ])
      .then(([appointments, timeOffs]) => {
        if (cancelled) return;
        setNextSlot(
          findNextOpenSlot({
            fromDay: formDay,
            daysAhead: NEXT_SLOT_SCAN_DAYS,
            resource,
            timeOffs,
            appointments,
            durationMinutes: totalDuration,
            excludeId: props.mode === 'edit' ? props.editing?.id : undefined,
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setNextSlot(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    props.visible,
    props.branchId,
    selectedResource,
    slotOptions.length,
    formDay,
    formDayIso,
    totalDuration,
    props.mode,
    props.editing?.id,
  ]);

  // Historial inline del cliente (lazy, silent fail).
  useEffect(() => {
    if (!props.visible || !customerId) {
      setCustomerStats(null);
      return;
    }
    let cancelled = false;
    void getCustomerAppointmentStats(customerId)
      .then((stats) => {
        if (!cancelled) setCustomerStats(stats);
      })
      .catch(() => {
        if (!cancelled) setCustomerStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [props.visible, customerId]);

  // Seña configurada en la sucursal (create) → info-box bajo Nota.
  useEffect(() => {
    if (!props.visible || !props.branchId || props.mode === 'edit') {
      setBookingSettings(null);
      return;
    }
    let cancelled = false;
    const branchId = props.branchId;
    void getBookingSettings(branchId)
      .then((settings) => {
        if (!cancelled) setBookingSettings(settings);
      })
      .catch(() => {
        if (!cancelled) setBookingSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [props.visible, props.branchId, props.mode]);

  const coveringEntitlements = useMemo(
    () => entitlements.filter((e) => entitlementCoversProducts(e, productIds)),
    [entitlements, productIds],
  );

  const selectedEntitlement =
    coveringEntitlements.find((e) => e.id === packageEntitlementId) ?? null;

  useEffect(() => {
    if (!props.visible || eligibleResources.length === 0) return;
    if (resourceId && eligibleResources.some((r) => r.id === resourceId)) return;
    setResourceId(eligibleResources[0]?.id ?? null);
  }, [props.visible, eligibleResources, resourceId]);

  useEffect(() => {
    if (!props.visible || slotOptions.length === 0) return;
    setTimeHm((prev) => {
      const snapped = snapToOpenSlot(prev, slotOptions);
      if (props.mode === 'create' && !props.prefill?.startsAt) {
        const today = new Date();
        if (sameLocalDay(formDay, today)) {
          const nowHm = toHm(today);
          const upcoming = slotOptions.find((hm) => hm >= nowHm);
          if (upcoming) return upcoming;
        }
      }
      return snapped;
    });
  }, [props.visible, slotOptions, props.mode, props.prefill?.startsAt, formDay]);

  const selectedClient = customerId ? clients.find((c) => c.id === customerId) ?? null : null;
  const endsPreview = totalDuration > 0 ? addMinutesHm(timeHm, totalDuration) : null;

  const clientOptions = useMemo(() => {
    const opts = clients.map((c) => ({
      id: c.id,
      name: c.name,
      subtitle:
        [c.phone ? formatArPhoneDisplay(c.phone) : '', c.email].filter(Boolean).join(' · ') || undefined,
    }));
    if (customerId && !clients.some((c) => c.id === customerId) && customerName.trim()) {
      opts.unshift({
        id: customerId,
        name: customerName.trim(),
        subtitle: customerPhone.trim()
          ? formatArPhoneDisplay(customerPhone) || customerPhone.trim()
          : 'Cliente vinculado',
      });
    }
    return opts;
  }, [clients, customerId, customerName, customerPhone]);

  const serviceOptions = useMemo(
    () =>
      services
        .filter((s) => !productIds.includes(s.id))
        .map((s) => ({
          id: s.id,
          name: s.name,
          subtitle: `${s.durationMinutes} min · ${formatMoney(s.price)}`,
        })),
    [services, productIds],
  );

  const staffOptions = useMemo(
    () => eligibleResources.map((r) => ({ id: r.id, name: r.displayName })),
    [eligibleResources],
  );

  const timeOptions = useMemo(
    () =>
      slotOptions.map((hm) => ({
        id: hm,
        name: hm,
        subtitle:
          totalDuration > 0 ? `Hasta ${addMinutesHm(hm, totalDuration) ?? '—'}` : undefined,
      })),
    [slotOptions, totalDuration],
  );

  const packageOptions = useMemo(
    () =>
      coveringEntitlements.map((e) => ({
        id: e.id,
        name: e.packageName,
        subtitle: `${e.sessionsRemaining} de ${e.sessionsTotal} · vence ${new Date(e.expiresAt).toLocaleDateString('es-AR')}`,
      })),
    [coveringEntitlements],
  );

  const applyClient = (id: string | null) => {
    if (!id) {
      setCustomerId(null);
      setPackageEntitlementId(null);
      return;
    }
    const c = clients.find((row) => row.id === id);
    if (!c) return;
    setCustomerId(c.id);
    setCustomerName(c.name);
    setCustomerPhone(nationalPartFromStored(c.phone || ''));
    setCustomerEmail(c.email || '');
    setPackageEntitlementId(null);
  };

  const clearClientLink = () => {
    setCustomerId(null);
    setPackageEntitlementId(null);
  };

  const submit = async () => {
    if (!props.orgId || !props.branchId || !primaryProductId || !resourceId || selectedServices.length === 0) {
      showError('Turno', 'Completá cliente, servicio y agenda.');
      return;
    }
    if (!customerName.trim()) {
      showError('Turno', 'Completá el nombre del cliente.');
      return;
    }
    const parsed = parseHm(timeHm);
    if (!parsed) {
      showError('Turno', 'Elegí un horario válido.');
      return;
    }
    if (!isEdit && slotOptions.length > 0 && !slotOptions.includes(timeHm)) {
      showError('Turno', 'Ese horario no está disponible (cerrado, ausente u ocupado).');
      return;
    }
    const starts = new Date(formDay);
    starts.setHours(parsed.h, parsed.m, 0, 0);
    const ends = new Date(starts.getTime() + totalDuration * 60_000);
    setSaving(true);
    try {
      if (props.mode === 'edit' && props.editing) {
        await updateAppointment({
          appointmentId: props.editing.id,
          resourceId,
          productId: primaryProductId,
          productIds,
          customerId,
          customerName,
          customerPhone,
          customerEmail,
          startsAt: starts.toISOString(),
          endsAt: ends.toISOString(),
          notes,
          amountTotal: selectedEntitlement ? 0 : totalPrice,
        });
      } else {
        await createWalkInAppointment({
          organizationId: props.orgId,
          branchId: props.branchId,
          resourceId,
          productId: primaryProductId,
          productIds,
          customerId,
          customerName,
          customerPhone,
          customerEmail,
          startsAt: starts.toISOString(),
          endsAt: ends.toISOString(),
          notes,
          amountTotal: selectedEntitlement ? 0 : totalPrice,
          packageEntitlementId: selectedEntitlement?.id ?? null,
          paymentMode: selectedEntitlement ? 'none' : undefined,
          paymentStatus: selectedEntitlement ? 'waived' : undefined,
        });
      }
      // Primer hito: el modal queda abierto con confirmación; el Listo cierra
      // vía onSaved (recarga agenda). Turnos 2+ → toast como hoy.
      const firstAppointmentPending =
        props.mode !== 'edit' &&
        useDay1PathNavStore.getState().phase === 'need_first_appointment';
      if (firstAppointmentPending) {
        setMilestoneDone({
          customerName: customerName.trim(),
          servicesLabel:
            selectedServices.length === 1
              ? selectedServices[0]!.name
              : `${selectedServices[0]!.name} + ${selectedServices.length - 1} más`,
          timeHm,
          endsHm: endsPreview,
          staffName: staffFirstName,
          priceLabel: selectedEntitlement
            ? 'Sesión de paquete'
            : totalPrice > 0
              ? formatMoney(totalPrice)
              : null,
        });
        hapticSuccess();
        trackOnboardingFunnel('appointment_created', { milestone: 'appointment' });
        invalidateDay1Evidence(props.branchId);
        // El nav store solo lo re-deriva useDay1Path (Home). Sin este avance,
        // los turnos siguientes en la misma sesión repetirían el hito.
        const nav = useDay1PathNavStore.getState();
        nav.setFromPath({
          mode: nav.mode,
          phase: 'celebrate_first_appointment',
          preferCatalogTab: nav.preferCatalogTab,
          collapseStorefrontNav: nav.collapseStorefrontNav,
        });
      } else {
        props.onSaved();
        showSuccessAfterModalClose(props.mode === 'edit' ? 'Turno actualizado' : 'Turno creado');
      }
    } catch (e) {
      presentUnexpectedFailure({ module: 'appointments', op: 'turno', error: e, title: 'Turno', userMessage: 'No se pudo guardar el turno.' });
    } finally {
      setSaving(false);
    }
  };

  const isEdit = props.mode === 'edit';
  const timeSlotValid = isEdit || (slotOptions.length > 0 && slotOptions.includes(timeHm));
  const canSubmit =
    Boolean(customerName.trim() && primaryProductId && resourceId && timeHm && selectedServices.length > 0) &&
    timeSlotValid &&
    !saving;

  // Hint honesto del CTA disabled: qué falta todavía.
  const missingBits: string[] = [];
  if (!customerName.trim()) missingBits.push('nombre');
  if (selectedServices.length === 0) missingBits.push('servicio');
  if (!resourceId) missingBits.push('agenda');
  if (!timeHm || !timeSlotValid) missingBits.push('horario');
  const canSubmitHint = missingBits.length > 0 ? `Falta: ${missingBits.join(' · ')}` : null;

  // Dirty = hay algo escrito/elegido que no es default del form.
  const formDirty = useMemo(() => {
    if (props.mode === 'edit' && props.editing) {
      const ed = props.editing;
      const edProductIds = (
        ed.items && ed.items.length > 0 ? ed.items.map((i) => i.productId) : [ed.productId]
      ).filter(Boolean);
      const orig = new Date(ed.startsAt);
      return (
        customerName !== ed.customerName ||
        customerPhone !== nationalPartFromStored(ed.customerPhone ?? '') ||
        customerEmail !== (ed.customerEmail ?? '') ||
        notes !== (ed.notes ?? '') ||
        resourceId !== ed.resourceId ||
        !sameLocalDay(formDay, orig) ||
        timeHm !== toHm(orig) ||
        JSON.stringify([...productIds].sort()) !==
          JSON.stringify([...edProductIds].sort()) ||
        packageEntitlementId !== (ed.packageEntitlementId ?? null)
      );
    }
    return Boolean(
      customerName.trim() ||
        customerPhone.trim() ||
        customerEmail.trim() ||
        notes.trim() ||
        productIds.length > 0,
    );
  }, [
    props.mode,
    props.editing,
    customerName,
    customerPhone,
    customerEmail,
    notes,
    resourceId,
    productIds,
    packageEntitlementId,
  ]);

  const requestClose = () => {
    if (saving) return;
    if (formDirty) {
      setDiscardVisible(true);
      return;
    }
    props.onClose();
  };

  const staffFirstName = selectedResource?.displayName?.split(/\s+/)[0] ?? '—';

  const clientPickValue = selectedClient?.name ?? (customerName.trim() || 'Elegí o escribí');
  const clientPickSubtitle = selectedClient
    ? (selectedClient.phone ? formatArPhoneDisplay(selectedClient.phone) : 'Cliente de la lista')
    : customerName.trim()
      ? 'Nombre a mano, sin ficha'
      : clients.length === 0
        ? 'Sin clientes. Escribí el nombre.'
        : undefined;

  const formDayLabel = dayOptionLabel(formDay, createTodayDate());

  const dayOptions = useMemo(() => {
    const today = createTodayDate();
    const out: Array<{ id: string; name: string }> = [];
    const seen = new Set<string>();
    const push = (d: Date) => {
      const iso = formatIsoDate(d);
      if (seen.has(iso)) return;
      seen.add(iso);
      out.push({ id: iso, name: dayOptionLabel(d, today) });
    };
    // Edit con fecha pasada o lejana: la opción original queda seleccionable.
    if (startOfLocalDay(formDay).getTime() < today.getTime()) push(formDay);
    for (let i = 0; i <= DAY_OPTIONS_AHEAD; i++) {
      push(new Date(today.getFullYear(), today.getMonth(), today.getDate() + i));
    }
    push(formDay);
    return out;
  }, [formDay]);

  // Edit: warn-box cuando el usuario movió día u hora vs el turno original.
  const movedDateTime = useMemo(() => {
    if (props.mode !== 'edit' || !props.editing) return false;
    const orig = new Date(props.editing.startsAt);
    return !sameLocalDay(formDay, orig) || timeHm !== toHm(orig);
  }, [props.mode, props.editing, formDay, timeHm]);

  const nextSlotLabel = useMemo(() => {
    if (!nextSlot) return null;
    const today = createTodayDate();
    const diff = Math.round((startOfLocalDay(nextSlot.day).getTime() - today.getTime()) / 86_400_000);
    const dayLabel = diff === 0 ? 'hoy' : diff === 1 ? 'mañana' : formatDayShort(nextSlot.day);
    return `${dayLabel} ${nextSlot.hm}`;
  }, [nextSlot]);

  // Info-box seña (proto v-form): create con seña configurada, o edit con seña paga.
  const depositInfoText = useMemo(() => {
    if (props.mode === 'edit') {
      if (props.editing?.paymentStatus !== 'deposit_paid') return null;
      const amt = props.editing.amountPaid ?? props.editing.amountTotal;
      return amt != null && amt > 0
        ? `Seña: ${formatMoney(amt)} ya cobrada para este turno.`
        : 'Seña ya cobrada para este turno.';
    }
    const mode = bookingSettings?.bookingPaymentMode;
    if (mode !== 'deposit' && mode !== 'full') return null;
    let label: string | null = null;
    if (mode === 'full') {
      label = totalPrice > 0 ? formatMoney(totalPrice) : 'el total del turno';
    } else if (bookingSettings?.bookingDepositAmount != null && bookingSettings.bookingDepositAmount > 0) {
      label = formatMoney(bookingSettings.bookingDepositAmount);
    } else if (bookingSettings?.bookingDepositPercent != null && bookingSettings.bookingDepositPercent > 0) {
      label =
        totalPrice > 0
          ? formatMoney(Math.round((totalPrice * bookingSettings.bookingDepositPercent) / 100))
          : `${bookingSettings.bookingDepositPercent}% del total`;
    }
    if (!label) return null;
    return `Seña: ${label} requerida para confirmar online (política de la sucursal). Turno de mostrador: se puede confirmar sin cobrar la seña acá.`;
  }, [props.mode, props.editing, bookingSettings, totalPrice]);

  const finishMilestone = () => {
    setMilestoneDone(null);
    props.onSaved();
  };

  // Primer turno (hito día 1): confirmación dentro del modal, sin toast.
  if (milestoneDone) {
    return (
      <FormShell
        visible={props.visible}
        isWide={props.isWide}
        title="Turno agendado"
        subtitle={formatDayLong(formDay)}
        onClose={finishMilestone}
        footer={
          <View style={[styles.modalActions, props.isWide && styles.modalActionsWide]}>
            <TouchableOpacity
              style={[styles.primaryBtn, props.isWide && styles.primaryBtnWide]}
              onPress={finishMilestone}
              accessibilityRole="button"
              accessibilityLabel="Listo"
              testID="appointments.form.milestoneDone"
            >
              <Text style={styles.primaryBtnText}>Listo</Text>
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.milestoneWrap}>
          <VueltiFeliz size={80} />
          <Text style={styles.milestoneName}>{milestoneDone.customerName}</Text>
          <Text style={styles.milestoneMeta}>
            {milestoneDone.servicesLabel}
            {milestoneDone.staffName !== '—' ? ` · ${milestoneDone.staffName}` : ''}
          </Text>
          <Text style={styles.milestoneMeta}>
            {milestoneDone.timeHm}
            {milestoneDone.endsHm ? `–${milestoneDone.endsHm}` : ''}
            {milestoneDone.priceLabel ? ` · ${milestoneDone.priceLabel}` : ''}
          </Text>
        </View>
      </FormShell>
    );
  }

  return (
    <>
      <FormShell
        visible={props.visible}
        isWide={props.isWide}
        title={isEdit ? 'Editar turno' : 'Nuevo turno'}
        subtitle={formatDayLong(formDay)}
        onClose={requestClose}
        overlay={
          <OperationalConfirmModal
            visible={discardVisible}
            presentation="overlay"
            title="Descartar cambios"
            kicker="Turno"
            message="Se pierden los datos que escribiste."
            confirmLabel="Descartar"
            destructive
            onClose={() => setDiscardVisible(false)}
            onConfirm={() => {
              setDiscardVisible(false);
              props.onClose();
            }}
          />
        }
        footer={
          <View>
            {!canSubmit && !saving && canSubmitHint ? (
              <Text style={styles.canSubmitHint}>{canSubmitHint}</Text>
            ) : null}
            <View style={[styles.modalActions, props.isWide && styles.modalActionsWide]}>
            {props.isWide ? (
              <TouchableOpacity
                style={styles.quietBtnWide}
                onPress={requestClose}
                disabled={saving}
                accessibilityRole="button"
                accessibilityState={{ disabled: saving }}
              >
                <Text style={styles.quietBtnText}>Cancelar</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={[
                styles.primaryBtn,
                props.isWide && styles.primaryBtnWide,
                !canSubmit && styles.primaryBtnDisabled,
              ]}
              onPress={() => void submit()}
              disabled={!canSubmit}
              accessibilityRole="button"
              accessibilityLabel={isEdit ? 'Guardar cambios' : 'Crear turno'}
              accessibilityState={{ disabled: !canSubmit }}
              testID="appointments.form.submit"
            >
              <Text style={[styles.primaryBtnText, !canSubmit && styles.primaryBtnTextDisabled]}>
                {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear turno'}
              </Text>
            </TouchableOpacity>
            {!props.isWide ? (
              <TouchableOpacity
                style={styles.quietBtn}
                onPress={requestClose}
                disabled={saving}
                accessibilityRole="button"
                accessibilityState={{ disabled: saving }}
              >
                <Text style={styles.quietBtnText}>Cancelar</Text>
              </TouchableOpacity>
            ) : null}
            </View>
          </View>
        }
      >
        <View style={styles.preview} accessibilityLiveRegion="polite">
          <View style={styles.timeBlock}>
            <Text style={styles.timeBlockHm}>
              {totalDuration > 0 ? totalDuration : '—'}
            </Text>
            <Text style={styles.timeBlockDur}>min</Text>
          </View>
          <View style={styles.previewCopy}>
            <Text style={styles.previewSvc} numberOfLines={2}>
              {selectedServices.length === 0
                ? 'Elegí un servicio'
                : selectedServices.length === 1
                  ? selectedServices[0]!.name
                  : `${selectedServices[0]!.name} + ${selectedServices.length - 1} más`}
            </Text>
            <Text style={styles.previewMeta} numberOfLines={1}>
              {staffFirstName}
              {endsPreview ? ` · hasta ${endsPreview}` : ''}
            </Text>
            {selectedServices.length > 0 ? (
              <Text style={styles.previewPrice}>
                {selectedEntitlement ? 'Sesión de paquete' : formatMoney(totalPrice)}
              </Text>
            ) : null}
          </View>
        </View>

        <View style={[styles.pickers, { marginBottom: 8 }]}>
          <PickRow
            label="Cliente"
            value={clientPickValue}
            subtitle={clientPickSubtitle}
            onPress={() => setOpenPicker('client')}
            expanded={openPicker === 'client'}
          />
          {customerStats ? (
            <Text style={styles.clientStats}>
              {customerStats.visits} visitas · {customerStats.noShows} no-shows
            </Text>
          ) : null}
        </View>
        <TextInput
          style={styles.input}
          value={customerName}
          onChangeText={(v) => {
            setCustomerName(v);
            if (customerId) clearClientLink();
          }}
          placeholder="Nombre y apellido"
          placeholderTextColor={agenda.ink2}
          autoFocus={Platform.OS === 'web' && !isEdit && !customerId}
          accessibilityLabel="Nombre del cliente"
          testID="appointments.form.customer-name"
          returnKeyType="next"
        />
        <TextInput
          style={styles.input}
          value={customerPhone}
          onChangeText={(v) => {
            setCustomerPhone(v);
            if (customerId) clearClientLink();
          }}
          placeholder="11 5555-5555 (opcional)"
          placeholderTextColor={agenda.ink2}
          keyboardType="phone-pad"
          accessibilityLabel="Teléfono del cliente"
        />
        {customerId ? (
          <Text style={styles.matchHint}>Cliente de la lista: {selectedClient?.name ?? customerName}</Text>
        ) : null}
        <TouchableOpacity
          style={styles.moreToggle}
          onPress={() => setShowMoreCustomer((v) => !v)}
          accessibilityRole="button"
          accessibilityState={{ expanded: showMoreCustomer || Boolean(customerEmail) }}
          accessibilityLabel={showMoreCustomer || customerEmail ? 'Ocultar email del cliente' : 'Agregar email del cliente'}
        >
          <Text style={styles.moreToggleText}>
            {showMoreCustomer || customerEmail ? 'Ocultar email' : 'Agregar email'}
          </Text>
        </TouchableOpacity>
        {showMoreCustomer || customerEmail ? (
          <TextInput
            style={styles.input}
            value={customerEmail}
            onChangeText={(v) => {
              setCustomerEmail(v);
              if (customerId) clearClientLink();
            }}
            placeholder="Email (opcional)"
            placeholderTextColor={agenda.ink2}
            keyboardType="email-address"
            autoCapitalize="none"
            accessibilityLabel="Email del cliente"
          />
        ) : null}

        <Text style={[styles.blockLabel, { marginTop: 6 }]}>Turno</Text>
        <View style={[styles.pickers, props.isWide && styles.pickersWide]}>
          <View style={props.isWide ? styles.pickerHalf : undefined}>
            <PickRow
              label="Fecha"
              value={formDayLabel}
              onPress={() => setOpenPicker('date')}
              expanded={openPicker === 'date'}
            />
          </View>
          <View style={props.isWide ? styles.pickerHalf : undefined}>
            <PickRow
              label="Agenda"
              value={selectedResource?.displayName ?? 'Elegí una agenda'}
              subtitle={
                eligibleResources.length === 0
                  ? 'Ninguna agenda hace este servicio'
                  : singleAgenda
                    ? 'Agenda de la sucursal'
                    : undefined
              }
              onPress={() => setOpenPicker('staff')}
              disabled={eligibleResources.length === 0}
              solo={singleAgenda}
              expanded={openPicker === 'staff'}
            />
          </View>
          <View style={props.isWide ? styles.pickerFull : undefined}>
            <PickRow
              label="Servicio"
              value={
                selectedServices.length === 0
                  ? 'Elegí un servicio'
                  : selectedServices.length === 1
                    ? selectedServices[0]!.name
                    : `${selectedServices.length} servicios`
              }
              subtitle={
                selectedServices.length > 0
                  ? `${totalDuration} min · ${formatMoney(totalPrice)}`
                  : services.length === 0
                    ? 'No hay servicios en el catálogo'
                    : undefined
              }
              onPress={() => setOpenPicker('service')}
              disabled={services.length === 0}
              expanded={openPicker === 'service'}
            />
            {selectedServices.length > 0 ? (
              <View style={{ gap: 6, marginTop: 8 }}>
                {selectedServices.map((s) => (
                  <View
                    key={s.id}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <Text style={{ flex: 1, color: agenda.ink, fontSize: 14 }} numberOfLines={1}>
                      {s.name} · {s.durationMinutes} min
                    </Text>
                    <TouchableOpacity
                      onPress={() => setProductIds((prev) => prev.filter((id) => id !== s.id))}
                      accessibilityLabel={`Quitar ${s.name}`}
                      hitSlop={8}
                    >
                      <Ionicons name="close-circle" size={20} color={agenda.ink2} />
                    </TouchableOpacity>
                  </View>
                ))}
                {serviceOptions.length > 0 ? (
                  <TouchableOpacity onPress={() => setOpenPicker('service')} hitSlop={6}>
                    <Text style={{ color: ticketColors.gold, fontWeight: '600', fontSize: 14 }}>
                      Sumá otro servicio
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : null}
          </View>
          <View style={props.isWide ? styles.pickerFull : undefined}>
            <PickRow
              label="Horario"
              value={timeHm}
              subtitle={
                slotOptions.length === 0
                  ? 'Sin horarios libres'
                  : endsPreview
                    ? `${totalDuration} min · termina ${endsPreview}`
                    : undefined
              }
              onPress={() => setOpenPicker('time')}
              disabled={slotOptions.length === 0}
              expanded={openPicker === 'time'}
            />
            {slotOptions.length === 0 && nextSlotLabel ? (
              <Text style={styles.nextSlotHint}>
                Próximo disponible: <Text style={styles.nextSlotStrong}>{nextSlotLabel}</Text>
                {` · slots libres según horario, ausencias y turnos de ${staffFirstName}.`}
              </Text>
            ) : null}
          </View>
        </View>

        {isEdit && movedDateTime ? (
          <View style={styles.warnBox}>
            <Text style={styles.warnBoxText}>
              Cambiar de día u hora avisa al profesional (push «reagendado») — igual que hoy.
            </Text>
          </View>
        ) : null}

        {hasServicePackages && customerId && coveringEntitlements.length > 0 && !isEdit ? (
          <View style={{ marginTop: 10 }}>
            <Text style={styles.blockLabel}>Paquete</Text>
            <PickRow
              label="Usar sesión de paquete"
              value={
                selectedEntitlement
                  ? `${selectedEntitlement.packageName} · ${selectedEntitlement.sessionsRemaining} rest.`
                  : 'No usar paquete'
              }
              subtitle={
                selectedEntitlement
                  ? `Vence ${new Date(selectedEntitlement.expiresAt).toLocaleDateString('es-AR')}`
                  : `${coveringEntitlements.length} disponible${coveringEntitlements.length === 1 ? '' : 's'}`
              }
              onPress={() => setOpenPicker('package')}
              expanded={openPicker === 'package'}
            />
          </View>
        ) : null}

        <Text style={[styles.blockLabel, { marginTop: 10 }]}>Nota</Text>
        <TextInput
          style={[styles.input, styles.notesInput]}
          value={notes}
          onChangeText={setNotes}
          placeholder="Interna — la ve el equipo, no el cliente"
          placeholderTextColor={agenda.ink2}
          multiline
          accessibilityLabel="Nota del turno"
          testID="appointments.form.notes"
        />
        {depositInfoText ? (
          <View style={styles.infoBox}>
            <Text style={styles.infoBoxText}>{depositInfoText}</Text>
          </View>
        ) : null}
      </FormShell>

      <MasterFilterSheetModal
        visible={openPicker === 'client'}
        title="Elegir cliente"
        options={clientOptions}
        selectedId={customerId}
        showAllOption
        allOptionLabel="Escribir nombre"
        appearance="ticket"
        onSelect={applyClient}
        onClose={() => setOpenPicker(null)}
        footerCreateLabel="Nuevo cliente"
        onFooterCreate={() => {
          setOpenPicker(null);
          openCustomerNewCustomer(navigation);
        }}
      />
      <MasterFilterSheetModal
        visible={openPicker === 'service'}
        title={productIds.length === 0 ? 'Elegir servicio' : 'Sumar servicio'}
        options={serviceOptions}
        selectedId={null}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) setProductIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
        }}
        onClose={() => setOpenPicker(null)}
        footerCreateLabel="Nuevo servicio"
        onFooterCreate={() => {
          setOpenPicker(null);
          openCatalogNewService(navigation);
        }}
      />
      <MasterFilterSheetModal
        visible={openPicker === 'staff'}
        title="Elegir agenda"
        options={staffOptions}
        selectedId={resourceId}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) setResourceId(id);
        }}
        onClose={() => setOpenPicker(null)}
        footerCreateLabel={canManageAgenda ? 'Nueva agenda' : undefined}
        onFooterCreate={canManageAgenda ? () => {
          setOpenPicker(null);
          openAppointmentResources(navigation);
        } : undefined}
      />
      <MasterFilterSheetModal
        visible={openPicker === 'date'}
        title="Elegir fecha"
        options={dayOptions}
        selectedId={formDayIso}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) setFormDay(parseIsoDate(id));
        }}
        onClose={() => setOpenPicker(null)}
      />
      <MasterFilterSheetModal
        visible={openPicker === 'time'}
        title="Elegir horario"
        options={timeOptions}
        selectedId={timeHm}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) setTimeHm(id);
        }}
        onClose={() => setOpenPicker(null)}
      />
      <MasterFilterSheetModal
        visible={openPicker === 'package'}
        title="Usar sesión de paquete"
        options={packageOptions}
        selectedId={packageEntitlementId}
        showAllOption
        allOptionLabel="No usar paquete"
        appearance="ticket"
        onSelect={(id) => {
          setPackageEntitlementId(id);
          setOpenPicker(null);
        }}
        onClose={() => setOpenPicker(null)}
      />
    </>
  );
}

function PickRow(props: {
  label: string;
  value: string;
  subtitle?: string;
  onPress: () => void;
  disabled?: boolean;
  solo?: boolean;
  expanded?: boolean;
}) {
  const interactive = !props.disabled && !props.solo;
  return (
    <TouchableOpacity
      style={[
        styles.pick,
        props.expanded && styles.pickExpanded,
        props.disabled && styles.pickDisabled,
      ]}
      onPress={props.onPress}
      disabled={!interactive}
      accessibilityRole="button"
      accessibilityState={{ disabled: !interactive, expanded: props.expanded }}
      accessibilityLabel={props.subtitle ? `${props.label}: ${props.value}. ${props.subtitle}` : `${props.label}: ${props.value}`}
      testID={`appointments.form.picker.${props.label.toLowerCase()}`}
    >
      <Text style={styles.pickK}>{props.label}</Text>
      <View style={styles.pickMain}>
        <Text style={styles.pickV} numberOfLines={1}>
          {props.value}
        </Text>
        {props.subtitle ? (
          <Text style={styles.pickSub} numberOfLines={1}>
            {props.subtitle}
          </Text>
        ) : null}
      </View>
      {props.solo ? (
        <Text style={styles.pickSoloHint}>La única</Text>
      ) : (
        <Ionicons name="chevron-down" size={18} color={agenda.ink2} />
      )}
    </TouchableOpacity>
  );
}

function FormShell(props: {
  visible: boolean;
  isWide: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
  /** Capa encima del sheet dentro del mismo Modal (confirmaciones). */
  overlay?: React.ReactNode;
}) {
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const toastHost = props.visible ? (
    <Toast config={appToastConfig} topOffset={toastTopOffset(insets.top)} />
  ) : null;

  const bottomPad = props.isWide ? spacing.sm : Math.max(insets.bottom, 24);
  const sheetMax = height * (props.isWide ? 0.94 : 0.92);
  const scrollMax = Math.max(160, sheetMax - 150 - bottomPad);

  const sheet = (
    <View
      style={[
        styles.sheet,
        props.isWide && styles.dialog,
        { maxHeight: sheetMax, paddingBottom: bottomPad },
      ]}
      testID="appointments.form.modal"
    >
      <View style={styles.formHeader}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.formTitle}>{props.title}</Text>
          {props.subtitle ? <Text style={styles.formSubtitle}>{props.subtitle}</Text> : null}
        </View>
        <TouchableOpacity onPress={props.onClose} hitSlop={8} accessibilityLabel="Cerrar">
          <Text style={styles.formClose}>✕</Text>
        </TouchableOpacity>
      </View>
      <ModalAwareScrollView
        style={[styles.formBody, { maxHeight: scrollMax }]}
        contentContainerStyle={styles.formBodyContent}
        keyboardShouldPersistTaps="handled"
        bottomOffset={spacing.sm}
      >
        {props.children}
      </ModalAwareScrollView>
      <View style={styles.formFooter}>{props.footer}</View>
    </View>
  );

  return (
    <Modal visible={props.visible} transparent animationType="fade" onRequestClose={props.onClose}>
      <View style={[styles.overlay, props.isWide && styles.overlayWide]}>
        {props.isWide ? (
          sheet
        ) : (
          <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
            {sheet}
          </KeyboardAvoidingView>
        )}
        {props.overlay}
      </View>
      {toastHost}
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  overlayWide: { justifyContent: 'center', alignItems: 'center', padding: 24 },
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: agenda.surface,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: '100%',
    overflow: 'hidden',
  },
  dialog: {
    ...agendaPaperSheet,
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH_FORM,
    borderRadius: 14,
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
    flexShrink: 0,
    gap: 8,
  },
  formTitle: { fontSize: 17, fontWeight: '700', color: agenda.ink, fontFamily: agendaFonts.sans },
  formSubtitle: {
    marginTop: 2,
    fontSize: 13,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  formClose: { fontSize: 18, color: agenda.ink2, padding: 4 },
  milestoneWrap: {
    alignItems: 'center',
    gap: 6,
    paddingVertical: spacing.lg,
  },
  milestoneName: {
    fontSize: 17,
    fontWeight: '800',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  milestoneMeta: {
    fontSize: 14,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    textAlign: 'center',
  },
  formBody: { flexGrow: 0, flexShrink: 1, minHeight: 0 },
  formBodyContent: { padding: 16, paddingBottom: 12 },
  formFooter: {
    flexShrink: 0,
    paddingHorizontal: 16,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  preview: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'stretch',
    marginBottom: 14,
  },
  timeBlock: {
    width: 84,
    flexShrink: 0,
    borderRadius: 10,
    backgroundColor: agenda.chooseBg,
    borderWidth: 1,
    borderColor: agenda.chooseBorder,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeBlockHm: {
    fontFamily: agendaFonts.mono,
    fontSize: 20,
    fontWeight: '700',
    color: agenda.chooseInk,
    letterSpacing: -0.5,
    lineHeight: 24,
  },
  timeBlockDur: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  previewCopy: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
    gap: 2,
  },
  previewSvc: {
    fontSize: 16,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    letterSpacing: -0.2,
    lineHeight: 22,
  },
  previewMeta: {
    fontSize: 13,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    lineHeight: 18,
  },
  previewPrice: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: '700',
    color: agenda.chooseInk,
    fontFamily: agendaFonts.sans,
  },
  blockLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: agenda.ink2,
    marginBottom: 8,
    fontFamily: agendaFonts.sans,
  },
  input: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: agenda.ink,
    backgroundColor: agenda.surface,
    fontFamily: agendaFonts.sans,
    marginBottom: 8,
  },
  matchHint: {
    fontSize: 12,
    fontWeight: '600',
    color: agenda.chooseInk,
    backgroundColor: agenda.chooseBg,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    marginBottom: 8,
    overflow: 'hidden',
    fontFamily: agendaFonts.sans,
  },
  moreToggle: {
    alignSelf: 'flex-start',
    minHeight: 36,
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 2,
    marginBottom: 10,
  },
  moreToggleText: {
    fontSize: 12,
    fontWeight: '700',
    color: agenda.brand,
    fontFamily: agendaFonts.sans,
  },
  pickers: { gap: 8 },
  pickersWide: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pickerHalf: { flexGrow: 1, flexBasis: '46%', minWidth: 220 },
  pickerFull: { flexBasis: '100%', width: '100%' },
  pick: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: 10,
    backgroundColor: agenda.surface2,
    paddingVertical: 11,
    paddingHorizontal: 12,
  },
  pickExpanded: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  pickDisabled: { opacity: 0.55 },
  pickK: {
    width: 72,
    flexShrink: 0,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  pickMain: { flex: 1, minWidth: 0, gap: 1 },
  pickV: {
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  pickSub: {
    fontSize: 11.5,
    fontWeight: '500',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  pickSoloHint: {
    fontSize: 11,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  clientStats: {
    marginTop: 6,
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  notesInput: {
    minHeight: 52,
    textAlignVertical: 'top',
  },
  nextSlotHint: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 17,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  nextSlotStrong: {
    fontWeight: '700',
    color: agenda.chooseInk,
    fontFamily: agendaFonts.sans,
  },
  // Proto .warn-box (misma receta que compras/gastos: warn tokens de ticket).
  warnBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    paddingVertical: 9,
    paddingHorizontal: 11,
    marginTop: 10,
  },
  warnBoxText: {
    color: ticketColors.warn,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '600',
    fontFamily: agendaFonts.sans,
  },
  // Proto .info-box: fondo tile neutro, no teal.
  infoBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  infoBoxText: {
    fontSize: 12.5,
    lineHeight: 19,
    color: ticketColors.ink2,
    fontFamily: agendaFonts.sans,
  },
  modalActions: { gap: 6 },
  modalActionsWide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  primaryBtn: {
    width: '100%',
    paddingVertical: 14,
    borderRadius: 11,
    backgroundColor: agenda.brand,
    alignItems: 'center',
  },
  primaryBtnWide: {
    flex: 1,
    width: undefined,
    paddingVertical: 12,
  },
  primaryBtnDisabled: {
    backgroundColor: agenda.surface2,
    borderWidth: 1,
    borderColor: agenda.border,
  },
  primaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: agenda.brandInk,
    fontFamily: agendaFonts.sans,
  },
  primaryBtnTextDisabled: { color: agenda.ink3 },
  canSubmitHint: {
    fontSize: 12,
    fontWeight: '500',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    textAlign: 'right',
    marginBottom: 6,
  },
  quietBtn: { width: '100%', paddingVertical: 10, alignItems: 'center' },
  quietBtnWide: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 11,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  quietBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
});
